import React, { FC, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Col, Container, FormGroup, FormText, Input, Label } from 'reactstrap';
import {
  PageHeader,
  PhoneInput,
  RegionSelector,
  UniqueAttributeInput,
  UserStateComponent,
} from '../../../components';
import { CountrySelector } from '../../../components/CountrySelector';
import { JSONForm } from '../../../components/json-form/JSONForm';
import { useTranslation } from '../../../translations';

export const ManualCreateComponent: FC = () => {
  const { ts } = useTranslation();
  const history = useHistory();
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [phone, setPhone] = useState('');

  const onSuccess = useCallback(
    (data: { id: number }) => {
      toast.success(ts('account_created'));
      history.push(`/housekeeping/attendees/user/${data.id}`);
    },
    [history, ts],
  );

  return (
    <UserStateComponent>
      <PageHeader>{ts('create_user.manual_account_creation')}</PageHeader>
      <Container className="justify-content-center" id="createManualUser">
        <JSONForm<{ id: number }> method="post" onSuccess={onSuccess} path="/api/users/manual">
          <FormGroup row>
            <Label for="reason" sm={4}>
              {ts('create_user.reason_for_manual_creation')}
            </Label>
            <Col sm={8}>
              <Input id="reason" name="reason" type="textarea" />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="username" sm={4}>
              {ts('common.username')}
            </Label>
            <Col sm={8}>
              <UniqueAttributeInput
                onChange={() => {
                  /* NO-OP */
                }}
                type="username"
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="email" sm={4}>
              {ts('common.email_address')}
            </Label>
            <Col sm={8}>
              <UniqueAttributeInput
                onChange={() => {
                  /* NO-OP */
                }}
                type="email"
              />
            </Col>
          </FormGroup>
          <hr />
          <FormGroup row>
            <Label for="firstName" sm={4}>
              {ts('common.first_name')}
            </Label>
            <Col sm={8}>
              <Input
                autoComplete="given-name"
                id="firstName"
                name="firstName"
                placeholder={`${ts('common.example')}: John`}
              />
              <FormText color="muted">{ts('common.this_must_be_your_legal_first')}</FormText>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="lastName" sm={4}>
              {ts('common.last_name')}
            </Label>
            <Col sm={8}>
              <Input
                autoComplete="family-name"
                id="lastName"
                name="lastName"
                placeholder={`${ts('common.example')}: Doe`}
              />
              <FormText color="muted">{ts('common.this_must_be_your_legal')}</FormText>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="preferredName" sm={4}>
              {ts('common.preferred_name')}
            </Label>
            <Col sm={8}>
              <Input
                autoComplete="username"
                id="preferredName"
                name="preferredName"
                placeholder={`${ts('common.example')}: Johnny`}
              />
              <FormText color="muted">{ts('common.if_you_have_a_name')}</FormText>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="bornAt" sm={4}>
              {ts('common.date_of_birth')}
            </Label>
            <Col sm={8}>
              <Input autoComplete="bday" id="bornAt" name="bornAt" type="date" />
            </Col>
          </FormGroup>
          <hr />
          <FormGroup row>
            <Label for="addressLine1" sm={4}>
              {ts('common.address_line_1')}
            </Label>
            <Col sm={8}>
              <Input
                autoComplete="address-line1"
                id="addressLine1"
                name="addressLine1"
                placeholder={`${ts('common.example')}: 123 Main St`}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="addressLine2" sm={4}>
              {ts('common.address_line_2_optional')}
            </Label>
            <Col sm={8}>
              <Input
                autoComplete="address-line2"
                id="addressLine2"
                name="addressLine2"
                placeholder={`${ts('common.example')}: Apt 123`}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="addressCity" sm={4}>
              {ts('common.city')}
            </Label>
            <Col sm={8}>
              <Input
                autoComplete="address-level2"
                id="addressCity"
                name="addressCity"
                placeholder={`${ts('common.example')}: Reno`}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="addressZipcode" sm={4}>
              {ts('common.zip_postal_code')}
            </Label>
            <Col sm={8}>
              <Input
                autoComplete="postal-code"
                id="addressZipcode"
                name="addressZipcode"
                placeholder={`${ts('common.example')}: 89501`}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="addressCountry" sm={4}>
              {ts('common.country')}
            </Label>
            <Col sm={8}>
              <CountrySelector
                autoComplete="country-name"
                className="form-control"
                id="addressCountry"
                name="addressCountry"
                onChange={(data) => {
                  setCountry(data);
                }}
                value={country}
                valueType="short"
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="addressState" sm={4}>
              {ts('common.state_province')}
            </Label>
            <Col sm={8}>
              <RegionSelector
                autoComplete="address-level1"
                className="form-control"
                country={country}
                countryValueType="short"
                data-coerce={false}
                disableWhenEmpty
                id="addressState"
                name="addressState"
                onChange={(data) => {
                  setRegion(data);
                }}
                value={region}
                valueType="short"
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="phone" sm={4}>
              {ts('common.phone_number')}
            </Label>
            <Col sm={8}>
              <PhoneInput name="phone" onChange={setPhone} value={phone} />
            </Col>
          </FormGroup>
          <FormGroup id="sendVerificationEmailGroup" row>
            <Col sm={4} />
            <Col sm={8}>
              <FormGroup check className="mt-10 start-align">
                <Input id="sendVerificationEmail" name="sendVerificationEmail" type="checkbox" />
                <Label check for="sendVerificationEmail">
                  {ts('create_user.send_the_user_an_account_confirmation_email')}
                  <FormText color="muted">
                    {ts('create_user.the_user_will_not_receive_additional_emails')}
                  </FormText>
                </Label>
              </FormGroup>
            </Col>
          </FormGroup>
          <FormGroup>
            <Button block color="primary" id="submitForm" type="submit">
              {ts('create_user.create_user')}
            </Button>
          </FormGroup>
        </JSONForm>
      </Container>
    </UserStateComponent>
  );
};
